import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import clsx from "clsx"
import { type ReactNode, useState } from "react"

import { theme } from "../../theme"
import { Text } from "../Text"
import styles from "./Tooltip.module.css"

export const TooltipProvider = TooltipPrimitive.Provider

interface BaseTooltipProps {
  children: ReactNode
  side?: Parameters<typeof TooltipPrimitive.Content>[0]["side"]
  sideOffset?: Parameters<typeof TooltipPrimitive.Content>[0]["sideOffset"]
  align?: Parameters<typeof TooltipPrimitive.Content>[0]["align"]
  delayDuration?: Parameters<typeof TooltipPrimitive.Root>[0]["delayDuration"]
  open?: boolean
  /**
   * Try not to use this.
   * You only need this with bad components like Tiptap's BubbleMenu which renders inline instead of using a Portal.
   * Remove this prop once this is done: https://linear.app/prismic/issue/PBD-1067/repeatable-zone-header-covers-link-dropdown
   * i.e, when the Tiptap BubbleMenu has been rewritten.
   */
  zIndexHack?: boolean
  visible?: boolean
  /**
   * By enabling the stable mount a `div` will be rendered around your trigger item.
   *
   * This is a workaround for a bug where mounting the Tooltip portal
   * inside another Portal rendered item causes the Tooltip to not open.
   *
   * @param stableMount
   * @defaultValue `false` This will wrap the trigger element in a Fragment.
   */
  stableMount?: boolean
  /**
   * Animation to use when showing/hiding the tooltip.
   *
   * @defaultValue "fade"
   */
  animation?: "fade" | "none"
}

interface TextTooltipProps extends BaseTooltipProps {
  variant?: "text"
  content: string
  title?: string
}

interface CustomTooltipProps extends BaseTooltipProps {
  variant: "custom"
  content: ReactNode
  title?: never
}

export type TooltipProps = TextTooltipProps | CustomTooltipProps

export function Tooltip(props: TooltipProps) {
  const {
    children,
    content,
    title,
    side,
    sideOffset,
    align,
    open: controlledOpen,
    zIndexHack = false,
    visible = true,
    stableMount = false,
    delayDuration = 300,
    variant = "text",
    animation = "fade"
  } = props

  const initialOpenState = visible ? controlledOpen ?? false : false
  const [open, setOpen] = useState(initialOpenState)

  function handleOnOpenChange(open: boolean) {
    if (!visible) return

    setOpen(open)
  }

  if (controlledOpen !== undefined && open !== controlledOpen)
    handleOnOpenChange(controlledOpen)

  const animationClass =
    animation !== "none" &&
    styles[`animation-${animation}-${open ? "entering" : "exiting"}`]

  // This is a workaround for a bug where mounting the Tooltip portal
  // inside another Portal rendered item causes the Tooltip to not open.
  const _children = stableMount ? <div>{children}</div> : children

  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      onOpenChange={handleOnOpenChange}
      open={open}
    >
      <TooltipPrimitive.Trigger asChild>{_children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          avoidCollisions
          className={clsx(
            styles.content,
            animationClass,
            styles[`content-${variant}`]
          )}
          style={{ zIndex: zIndexHack ? 100 : undefined }}
          collisionPadding={parseFloat(theme.space[16])}
          side={side}
          sideOffset={sideOffset}
          align={align}
        >
          {variant === "text" ? (
            <>
              {title && (
                <Text color="white" variant="smallBold">
                  {title}
                </Text>
              )}

              <Text color="white" variant="small">
                {content}
              </Text>

              <TooltipPrimitive.Arrow className={styles.arrow} />
            </>
          ) : (
            content
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
